import { capitalize, unref } from 'vue'
import * as Sentry from '@sentry/browser'
import dayjs from 'dayjs'
import { isEqual, sortBy } from 'lodash'

export function formatDollars(value = 0) {

  // Between 0 and $1, we want to make sure we render 10 cents as $0.10 not $0.1
  let dollarValue
  if (value < 1 && value > 0) {
    dollarValue = value.toFixed(2)
  } else {
    dollarValue = value
  }
  const commaSeparatedValue = dollarValue.toLocaleString('en-US')
  return `$${commaSeparatedValue}`
}

export function formatDecimalToPercent(val, decimalPlaces = 0) {
  if (val) {
    const percentValue = (val * 100).toFixed(decimalPlaces)
    return `${percentValue}%`
  }
  return '0%'
}

export function formatComma(value) {
  if (value === undefined || value === null) {
    return value
  }
  return value.toLocaleString('en-US')
}

export function handleError(error) {
  /**
  * Send an error message to Sentry.
  * This should be the default method used in all `catch blocks`
  * @param error Pass the error that is thrown
  * @return There is no return value
  */
  Sentry.captureException(error)
  console.error(error)
}

export function roundToDecimalPlaces(value, decimalPlaces = 2) {
  const multiplier = 10 ** decimalPlaces
  return Math.round((value + Number.EPSILON) * multiplier) / multiplier
}

export function highlight(search='', text='') {
  if (search.length > 1) {
    const regex = new RegExp(search, 'ig')
    const matches = text.match(regex)
    if (matches) {
      matches.forEach(match => {
        text = text.replace(match, `<span class='bg-orange'>${match}</span>`)
      })
    }
  }
  return text
}

function isPrimitive(value) {
  return value !== Object(value)
}

export function objectToFormData(obj, formData = new FormData(), parentKey = '') {
  if (obj === null || obj === undefined) return formData

  if (Array.isArray(obj)) {
    if (obj.length === 0) {
      return formData // Skip appending if the array is empty
    }

    const isArrayOfPrimitives = obj.every(isPrimitive)

    if (isArrayOfPrimitives) {
      // Join the array of primitives into a single string with comma separation
      formData.append(parentKey, obj.join(','))
    } else {
      obj.forEach((value, index) => {
        const key = `${parentKey}[${index}]`
        if (typeof value === 'object' && value !== null) {
          objectToFormData(value, formData, key)
        } else {
          formData.append(key, value)
        }
      })
    }
  } else if (typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof File) && !(obj instanceof Blob)) {
    Object.keys(obj).forEach(key => {
      const value = obj[key]
      const fullKey = parentKey ? `${parentKey}[${key}]` : key

      if (value === null || value === undefined) return

      if (typeof value === 'object') {
        objectToFormData(value, formData, fullKey)
      } else {
        formData.append(fullKey, value)
      }
    })
  } else {
    formData.append(parentKey, obj)
  }

  return formData
}

export function returnAsArray(refValue) {
  const value = unref(refValue)
  return value ? (Array.isArray(value) ? value : [value]) : []
}

export function getArticle(word) {
  word = word.toLowerCase()

  if (/^[aeiou]/.test(word)) {
    return 'an'
  }
  return 'a'
}

/**
 * Converts a given string to Title Case.
 *
 * This function handles multiple string formats including:
 * - camelCase
 * - PascalCase
 * - snake_case
 * - kebab-case
 *
 * It replaces underscores and dashes with spaces, inserts spaces between lowercase and uppercase letters,
 * and capitalizes the first letter of each word.
 *
 * @param {string} inputStr - The string to convert to Title Case. This string can be in camelCase, PascalCase, snake_case, or kebab-case.
 * @returns {string} - The converted string in Title Case format.
 *
 * @example
 * toTitleCase('camelCase'); // Returns 'Camel Case'
 * toTitleCase('PascalCase'); // Returns 'Pascal Case'
 * toTitleCase('snake_case'); // Returns 'Snake Case'
 * toTitleCase('kebab-case'); // Returns 'Kebab Case'
 */
export function toTitleCase(inputStr) {
  return inputStr
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .map(capitalize)
    .join(' ')
}

export function formatDate(timestamp, dateFormat = 'MM/DD/YYYY') {
  return dayjs(timestamp).isValid() ? dayjs(timestamp).format(dateFormat) : ''
}
export function isSameElements(array1, array2) {
  return isEqual(sortBy(array1), sortBy(array2))
}
